import { FC, useEffect, useMemo } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { useField } from 'formik';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { OptionTypes } from 'src/components/Dropdown';
import { DropDown } from 'src/components/FormikComponents';
import MainCard from 'src/components/MainCard/MainCard';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useProductOrderContext } from 'src/contexts/ProductOrderProvider';
import { useServiceOrderContext } from 'src/contexts/ServiceOrderProvider';
import { GET_PRICING_OFFER } from 'src/graphql/queries/pricing.query';
import { usePricingOffer } from 'src/hooks/usePricingOffer';

import { showErrorToast } from '../Toasts/toast';

import { ContractTerm, PricingServiceTypes, PricingTerms, ServiceTypes } from './types';

interface AddServiceTypeProps {
    contractTerms: ContractTerm[];
    hasExistingService: boolean;
}

const AddServiceType: FC<AddServiceTypeProps> = ({ contractTerms, hasExistingService }) => {
    const [{ value: serviceValue }] = useField('service');
    const [, , { setValue: setCustomTermValue }] = useField('customTerm');
    const [, , { setValue: setPromoOfferValue }] = useField('service.promoOffer');
    const [
        ,
        { touched: isShouldApplyPromoTouched },
        { setTouched: setShouldApplyPromoTouch, setValue: setShouldApplyPromo },
    ] = useField('service.shouldApplyPromo');
    const { serviceType } = useProductOrderContext();
    const { partnerId, setContractTerm, contractTerm } = useServiceOrderContext();
    const [getPricingOffer, { data: pricingOfferData }] = useLazyQuery(GET_PRICING_OFFER, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    const { pricingOfferTerminalOrOnlineOptions } = usePricingOffer({
        partnerId,
        contractTerm,
        serviceType: serviceValue?.serviceType,
    });
    const selectedServiceType = serviceValue?.serviceType;
    const isOnlinePricingOffer = selectedServiceType === PricingServiceTypes.ONLINE;

    const serviceTypes = [
        {
            label: ServiceTypes.TERMINAL,
            value: PricingServiceTypes.TERMINAL,
        },
        {
            label: ServiceTypes.ONLINE,
            value: PricingServiceTypes.ONLINE,
        },
        {
            label: ServiceTypes.ACQUIRING_ONLY,
            value: PricingServiceTypes.ACQUIRING_ONLY,
        },
    ];
    const { merchantApplication } = useAddMerchantContext();

    const { setPartnerId } = useServiceOrderContext();

    useEffect(() => {
        if (merchantApplication) {
            setPartnerId(merchantApplication.partnerId);
        }
    }, [merchantApplication]);

    const selectedOfferHasPromo = useMemo(() => {
        let selectedOfferValue;
        if (serviceType === PricingServiceTypes.TERMINAL) {
            selectedOfferValue = serviceValue.selectedTerminalOffer;
        } else if (serviceType === PricingServiceTypes.ONLINE) {
            selectedOfferValue = serviceValue.selectedOnlineOffer;
        }
        const selectedOffer = pricingOfferTerminalOrOnlineOptions.find(({ value }) => {
            return value === selectedOfferValue;
        });
        return selectedOffer?.shouldApplyPromo;
    }, [serviceType, pricingOfferTerminalOrOnlineOptions, serviceValue]);

    const contractTermOptions: OptionTypes[] = useMemo(() => {
        if (
            serviceValue?.serviceType === PricingServiceTypes.ONLINE ||
            serviceValue?.serviceType === PricingServiceTypes.TERMINAL
        ) {
            const { shouldApplyPromo } = serviceValue;

            return contractTerms
                .filter((term) => {
                    if (serviceValue?.serviceType === PricingServiceTypes.ONLINE) {
                        return term.online;
                    }
                    return true;
                })
                .map((term) => {
                    if (shouldApplyPromo === 'no' || !selectedOfferHasPromo) {
                        return { value: term.value, label: term.label };
                    }
                    return { value: term.value, label: `${term.label} + 3 Months` };
                });
        }

        return contractTerms.map((term) => {
            return {
                value: term.value,
                label: term.label,
            };
        });
    }, [contractTerms, serviceValue, selectedOfferHasPromo]);

    useEffect(() => {
        if (pricingOfferData?.offer?.customTerm) {
            setCustomTermValue(pricingOfferData.offer.customTerm);
        } else {
            setCustomTermValue(undefined);
        }
    }, [pricingOfferData]);

    useEffect(() => {
        if (!selectedOfferHasPromo && isShouldApplyPromoTouched) {
            setPromoOfferValue(undefined);
            setShouldApplyPromo(undefined);
        }
        // Prevent running on load
        setShouldApplyPromoTouch(true);
    }, [selectedOfferHasPromo]);

    const serviceTypeOptions = serviceTypes.map((option) => {
        return {
            value: option.value,
            label: option.label,
        };
    });

    const textColor = '#575757';

    return (
        <MainCard>
            <Typography fontSize="24px" color={textColor} marginBottom="8px">
                Service Type
            </Typography>
            <Typography fontSize="14px" color={textColor} marginBottom="8px">
                Select merchant service type
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '34px',
                }}
            />
            <Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <DropDown
                            name="service.serviceType"
                            label="Service Type"
                            options={serviceTypeOptions}
                            disabled={hasExistingService}
                            placeholder="Select Service Type"
                        />
                    </Grid>

                    {serviceType !== PricingServiceTypes.ACQUIRING_ONLY && (
                        <>
                            <Grid item xs={12} md={6}>
                                <DropDown
                                    options={contractTermOptions}
                                    name="service.contractTerm"
                                    label="Contract Term"
                                    onChange={(value) => {
                                        return setContractTerm(value);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DropDown
                                    name={`${
                                        isOnlinePricingOffer
                                            ? 'service.selectedOnlineOffer'
                                            : 'service.selectedTerminalOffer'
                                    }`}
                                    label={`${
                                        isOnlinePricingOffer ? 'Online' : 'Terminal'
                                    } Pricing Offer`}
                                    options={pricingOfferTerminalOrOnlineOptions}
                                    placeholder={`Select ${
                                        isOnlinePricingOffer ? 'Online' : 'Terminal'
                                    } Pricing Offer`}
                                />
                            </Grid>
                        </>
                    )}
                    {selectedOfferHasPromo && (
                        <Grid item xs={12} md={6}>
                            <DropDown
                                name="service.shouldApplyPromo"
                                label="Promo Offer?"
                                defaultValue={serviceValue?.shouldApplyPromo ?? 'no'}
                                options={[
                                    { value: 'no', label: 'NO' },
                                    { value: 'yes', label: 'YES' },
                                ]}
                            />
                        </Grid>
                    )}

                    {serviceValue.shouldApplyPromo === 'yes' && (
                        <Grid item xs={12} md={6}>
                            <DropDown
                                name="service.promoOffer"
                                label="Select Promo Offer"
                                placeholder="Select Promo Offer"
                                options={[
                                    { value: 3, label: '3 Months Free' },
                                    { value: 6, label: '6 Months Half Price' },
                                ]}
                            />
                        </Grid>
                    )}
                    {pricingOfferData?.offer?.customTerm &&
                    serviceValue?.contractTerm === PricingTerms.CUSTOM ? (
                        <Grid item xs={12} md={6}>
                            <InputBaseField
                                label="Custom Term in Months"
                                name="customTerm"
                                placeholder="Enter Custom Term in Months"
                                value={pricingOfferData?.offer?.customTerm ?? ''}
                                disabled
                            />
                        </Grid>
                    ) : null}
                </Grid>
            </Box>
        </MainCard>
    );
};
export default AddServiceType;
